import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { take } from "rxjs/operators";

import { AppState } from "@skills-app/root.reducer";
import ICertification from "@skills-app/models/certification";
import { ICapability } from "@skills-app/models/capability";
import { IIndustryItem } from "@skills-app/models/industry";
import { ISkillNode } from "@skills-app/models/skillNode";
import {
  ConsultantAnyFilterActions,
  ConsultantNameFilterActions,
  ConsultantRoleFilterActions,
  IndustrySearchBarActions,
  PrimaryCapabilitySearchBarActions,
  SearchBySkillOrConsultantApiActions,
  SearchBySkillOrConsultantPageActions,
  SkillSearchBarActions,
} from "@skills-app/consultant-skill/actions";
import * as fromConsultantSearchReducer from "@skills-app/consultant-skill/reducers/consultant-search.reducer";
import { ActiveFilterSummaryItem } from "@skills-app/consultant-skill/utils/search-state-persistence";
import { CertificationSearchBarActions } from "@skills-app/shared/actions";

@Component({
  standalone: false,
  selector: "app-search-active-filters-summary",
  templateUrl: "./search-active-filters-summary.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./search-active-filters-summary.component.css"],
})
export class SearchActiveFiltersSummaryComponent {
  @Input() items: ActiveFilterSummaryItem[] | null = [];
  @Input() clearButtonLabel = "Clear all filters";

  @Output() clearAll = new EventEmitter<void>();

  constructor(private readonly store: Store<AppState>) {}

  removeItem(item: ActiveFilterSummaryItem): void {
    if (!item) {
      return;
    }

    this.store
      .select(fromConsultantSearchReducer.getConsultantSearchState)
      .pipe(take(1))
      .subscribe((state) => {
        let handled = true;

        switch (item.kind) {
          case "lob": {
            const removeOrgIds = new Set(item.removeOrgIds ?? []);
            const removeOrgNames = new Set(
              (item.removeOrgNames ?? []).map((name) => name.toLowerCase()),
            );

            const remaining = (state.selectedOrganisations ?? []).filter(
              (org) => {
                const id = String((org as any)?.Id ?? "").trim();
                const name = String((org as any)?.Name ?? "")
                  .trim()
                  .toLowerCase();

                if (id.length > 0) {
                  return !removeOrgIds.has(id);
                }

                return !removeOrgNames.has(name);
              },
            );

            this.store.dispatch(
              SearchBySkillOrConsultantPageActions.filterConsultantsByOrg({
                orgs: remaining,
              }),
            );
            break;
          }

          case "location": {
            const remaining = (state.selectedLocations ?? []).filter(
              (location) => String(location ?? "").trim() !== item.value,
            );

            this.store.dispatch(
              SearchBySkillOrConsultantPageActions.filterConsultantsByLocation({
                locations: remaining,
              }),
            );
            break;
          }

          case "role": {
            this.store.dispatch(
              ConsultantRoleFilterActions.removeRoleFromSearch({
                role: item.value,
              }),
            );
            break;
          }

          case "skill": {
            const skill = (state.selectedSkills ?? []).find(
              (selectedSkill) =>
                String((selectedSkill as any)?.Id ?? "").trim() ===
                item.skillId,
            ) as ISkillNode | undefined;

            if (!skill) {
              handled = false;
              break;
            }

            this.store.dispatch(
              SkillSearchBarActions.removeSkillFromSearch({
                skill,
              }),
            );
            break;
          }

          case "certification": {
            const certification = (state.selectedCertifications ?? []).find(
              (selectedCertification) =>
                String((selectedCertification as any)?.Id ?? "").trim() ===
                item.certificationId,
            ) as ICertification | undefined;

            if (!certification) {
              handled = false;
              break;
            }

            this.store.dispatch(
              CertificationSearchBarActions.removeCertificationFromSearch({
                certification,
              }),
            );
            break;
          }

          case "capability": {
            const capability = (state.selectedCapabilities ?? []).find(
              (selectedCapability) =>
                String((selectedCapability as any)?.Id ?? "").trim() ===
                item.capabilityId,
            ) as ICapability | undefined;

            if (!capability) {
              handled = false;
              break;
            }

            this.store.dispatch(
              PrimaryCapabilitySearchBarActions.removePrimaryCapabilityFromSearch(
                {
                  capability,
                },
              ),
            );
            break;
          }

          case "industry": {
            const industryItem = (state.selectedIndustryItems ?? []).find(
              (selectedIndustryItem) =>
                typeof (selectedIndustryItem as any)?.IndustryOptions ===
                  "number" &&
                (selectedIndustryItem as any).IndustryOptions ===
                  item.industryOption,
            ) as IIndustryItem | undefined;

            if (!industryItem) {
              handled = false;
              break;
            }

            this.store.dispatch(
              IndustrySearchBarActions.removeIndustryItemFromSearch({
                industryItem,
              }),
            );
            break;
          }

          case "name": {
            this.store.dispatch(
              ConsultantNameFilterActions.setFilter({
                filterString: null as any,
              }),
            );
            break;
          }

          case "searchAny": {
            this.store.dispatch(
              ConsultantAnyFilterActions.setFilter({
                filterString: null as any,
              }),
            );
            break;
          }

          case "updatedWithin": {
            this.store.dispatch(
              SearchBySkillOrConsultantPageActions.setUpdatedWithinMonths({
                months: null,
              }),
            );
            break;
          }

          case "includeRelatedSkills": {
            if (state.includeRelatedSkills) {
              this.store.dispatch(
                SearchBySkillOrConsultantPageActions.showRelatedSkills(),
              );
            } else {
              handled = false;
            }
            break;
          }

          case "includeExpiredCerts": {
            if (state.includeExpiredCerts) {
              this.store.dispatch(
                SearchBySkillOrConsultantPageActions.showExpiredCerts(),
              );
            } else {
              handled = false;
            }
            break;
          }

          case "hideNoProfile": {
            if (state.hideNoProfile) {
              this.store.dispatch(
                SearchBySkillOrConsultantPageActions.toggleHideNoProfile(),
              );
            } else {
              handled = false;
            }
            break;
          }

          default:
            handled = false;
            break;
        }

        if (!handled) {
          return;
        }

        this.store.dispatch(
          SearchBySkillOrConsultantApiActions.requestFilteredConsultants(),
        );
      });
  }
}
